import WarpElement from "@warp-ds/elements-core";
import { SearchFilter } from "../../../models/filters.js";
import { css, html, nothing } from "lit";
import { Subvertical } from "../../../constants.js";
import { i18n } from "@lingui/core";

interface ModelCountMap {
    [key: string]: number;
}

const defaultModelCount = 4;
const maxModelCount = 10;

class MakeWithModelLinks extends WarpElement {
    declare makeModelLinks: SearchFilter[];
    declare subvertical: Subvertical;
    declare modelCountMap: ModelCountMap;
    declare makesToShow: number;

    static styles = [
        WarpElement.styles,
        css`
            @css-placeholder-9uXGsH;
        `,
    ];

    static properties = {
        modelCountMap: { type: Object },
        makeModelLinks: { type: Array },
        subvertical: { type: String },
        showMoreMakes: { type: Boolean },
        makesToShow: { type: Number },
    };

    constructor() {
        super();
        this.modelCountMap = {};
    }

    connectedCallback() {
        super.connectedCallback();
    }

    increaseModelsToShow(make: string) {
        const currentModelCount = this.modelCountMap?.[make];
        const newModelCount = Math.min((currentModelCount ?? defaultModelCount) + 3, maxModelCount);
        this.modelCountMap = { ...this.modelCountMap, [make]: newModelCount };
    }

    resetModelsToShow(make: string) {
        this.modelCountMap = {
            ...this.modelCountMap,
            [make]: defaultModelCount,
        };
    }

    render() {
        return html`<div class="grid grid-cols-5 lt-md:grid-cols-2 md:gap-y-32 md:gap-x-56 gap-24">
            ${this.makeModelLinks.map(({ title, links }, index) => {
                const titleText = typeof title === "string" ? title : title?.displayName;
                const titleUrl = typeof title === "string" ? "" : title?.url;
                const modelsToShow = this.modelCountMap?.[titleText] ?? defaultModelCount;
                return html`<ul
                    id="${this.subvertical}-make-list"
                    class="my-8 flex flex-col gap-4 ${index >= this.makesToShow ? "hidden" : ""}"
                >
                    ${title
                        ? titleUrl
                            ? html`<label for="${this.subvertical}-make-list" class="font-bold"
                                  ><a href="${titleUrl}" class="s-text">${titleText}</a></label
                              >`
                            : html`<label for="${this.subvertical}-make-list" class="font-bold s-text">${titleText}</label>`
                        : nothing}
                    ${links.map(({ displayName: model, url }, i) => {
                        const canShowMore = maxModelCount > modelsToShow && links.length > modelsToShow;
                        const canShowLess = maxModelCount === modelsToShow;

                        const extraButton = () => {
                            switch (true) {
                                case i === modelsToShow - 1 && canShowMore:
                                    return html`<li>
                                        <w-button variant="link" @click="${() => this.increaseModelsToShow(titleText)}"
                                            >${i18n.t({
                                                id: "make-model-links.column.show-more",
                                                message: "Show more",
                                            })}
                                        </w-button>
                                    </li>`;

                                case i === modelsToShow - 1 && canShowLess:
                                    return html`<li>
                                        <w-button variant="link" @click="${() => this.resetModelsToShow(titleText)}"
                                            >${i18n.t({
                                                id: "make-model-links.column.show-less",
                                                message: "Show less",
                                            })}
                                        </w-button>
                                    </li>`;

                                default:
                                    return nothing;
                            }
                        };

                        return html`<li class="${i >= modelsToShow ? "hidden" : ""}">
                                <a href="${url}" class="s-text s-text-subtle">${model}</a>
                            </li>
                            ${extraButton()}`;
                    })}
                </ul>`;
            })}
        </div>`;
    }
}

customElements.define("mfp-make-with-model-links", MakeWithModelLinks as unknown as CustomElementConstructor);
